import useOptimisticMoveProjectToPipelineStage from '@/api/mutations/project/optimistic/useOptimisticMoveProjectToPipelineStage';
import useGetPipeline from '@/api/queries/pipelines/useGetPipeline';
import useGetProjectPotentialPipelineTransitions from '@/api/queries/project/useGetProjectPotentialPipelineTransitions';
import { getDisallowedTransitionsSet } from '@/helpers/pipelinesHelper';
import { Combobox, SelectSizeVariant } from '@payaca/ambrosia';

import { FC } from 'react';
import { useParams } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../../../api/queries/project/useGetProjectByIdOverview';

const PipelineStageControl: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project } = useGetProjectByIdOverview(projectId);

  if (!projectId) return null;

  return (
    <V2PipelineStageControl
      projectId={projectId}
      pipelineId={project?.pipeline?.publicId}
    />
  );
};

const V2PipelineStageControl: FC<{
  projectId: string;
  pipelineId?: string;
}> = ({ projectId, pipelineId }) => {
  const {
    potentialPipelineTransitions,
    isLoading: isLoadingPotentialPipelineTransitions,
  } = useGetProjectPotentialPipelineTransitions(projectId);
  const { data: pipelineData } = useGetPipeline(pipelineId);
  const { project } = useGetProjectByIdOverview(projectId);
  const { mutateAsync } = useOptimisticMoveProjectToPipelineStage(pipelineId);

  const disallowedTransitionsSet = potentialPipelineTransitions
    ? getDisallowedTransitionsSet(potentialPipelineTransitions)
    : new Set<string>();

  if (!project) return null;

  const options = pipelineData?.pipeline?.stages.map((stage) => ({
    value: stage.publicId,
    label: stage.title,
    disabled:
      isLoadingPotentialPipelineTransitions ||
      disallowedTransitionsSet.has(stage.publicId),
  })) ?? [
    {
      value: project.currentPipelineStage.publicId,
      label: project.currentPipelineStage.title,
    },
  ];

  return (
    <Combobox
      CustomSelected={({ selectedOptions }) =>
        selectedOptions[0] ? (
          <>
            <b>Stage:</b> {selectedOptions[0]?.label}
          </>
        ) : null
      }
      sizeVariant={SelectSizeVariant.SM}
      value={project.currentPipelineStage.publicId}
      options={options}
      onChange={(value) => {
        if (Array.isArray(value) || !value) return;
        if (!project.currentPipelineStage.publicId) return;

        void mutateAsync({
          projectPublicId: projectId,
          targetPipelineStagePublicId: value,
          currentPipelineStagePublicId: project.currentPipelineStage.publicId,
        });
      }}
    />
  );
};

export default PipelineStageControl;
